import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import { useAuth } from '~/auth/auth-context'
import { useReturnScroll } from '~/lib/opening-set'
import { AmbientGlow, AmbientScope } from '~/components/ambient-glow'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { EdgeSteps } from '~/components/edge-steps'
import { EmptyState } from '~/components/empty-state'
import { DaySummaryTrigger } from '~/components/day-summary'
import { timeOf } from '~/data/nutrition'
import { WeighInDayPhotos } from '~/components/weighin-recap'
import { HERO_FACE_SIZES } from '~/components/hero-face'
import {
  BodyweightIcon,
  CheckIcon,
  ClockIcon,
  CloseIcon,
  DeleteIcon,
  EditIcon,
  LayersIcon,
  ListIcon,
  ScaleIcon,
  ViewAgendaIcon,
} from '~/components/icons'
import { SetBlock } from '~/components/set-block'
import { SetGrid } from '~/components/set-grid'
import { SetDeck } from '~/components/set-deck'
import { Skeleton, SkeletonBlock } from '~/components/skeleton'
import { useToast } from '~/components/toast-context'
import { WorkoutPoster } from '~/components/workout-thumbnail'
import type { LoggedSet, Workout } from '~/data/types'
import { dayKey, formatDayLabel } from '~/lib/dates'
import { setIsShown } from '~/lib/set-filters'
import { BLOCK_SPACING } from '~/lib/rhythm'
import { useMovingTiles } from '~/lib/moving-tiles'
import { exerciseBlocksOf, numbersInRun } from '~/lib/set-runs'
import {
  setWorkoutSetsLayout,
  useWorkoutSetsLayout,
  type WorkoutSetsLayout,
} from '~/lib/workout-sets-layout'
import { useDataService, useDataState } from '~/data/data-context'
import type { Lookup } from '~/data/service'
import {
  leadDetail,
  summaryStats,
  workoutNotes,
  workoutWhen,
  type WorkoutStat,
} from '~/lib/set-summary'
import { workoutTileIds } from '~/lib/workout-pictures'
import { markPageTransitionSource, pageTransitionName } from '~/lib/view-transitions'

/** The glyph that says at a glance which count a pill is carrying. */
const STAT_GLYPHS: Record<WorkoutStat['id'], typeof LayersIcon> = {
  sets: LayersIcon,
  exercises: ListIcon,
  moved: ScaleIcon,
  reps: BodyweightIcon,
}

export const Route = createFileRoute('/workouts/$workoutId')({
  component: WorkoutViewer,
})

function WorkoutViewer() {
  // A set opened from this page flies back to a page standing where it was left.
  useReturnScroll()
  const { workoutId } = Route.useParams()
  // Read rather than merely subscribed to: renames and deletions re-render off this, and the
  // workouts either side of this one are found in it.
  const state = useDataState()
  const service = useDataService()
  const navigate = useNavigate()
  const toast = useToast()
  const { units, distanceUnits } = useAuth()

  const [deleting, setDeleting] = useState(false)

  /*
   * How the sets are laid out — by exercise or in order — which is one answer for every
   * workout's page. Read before anything is drawn: the store is the device's own and answers at
   * once, so the page never lays itself out one way and then moves to the other.
   */
  const layout = useWorkoutSetsLayout()
  // Whether the layout has been changed since this page was opened, which is when headings fade.
  const [relaidOut, setRelaidOut] = useState(false)
  const setsRoot = useRef<HTMLDivElement>(null)
  const beforeLayoutChange = useMovingTiles(setsRoot, layout)

  useEffect(() => {
    markPageTransitionSource('workout', workoutId)
  }, [workoutId])

  /*
   * The workouts either side of this one, in the order the timeline puts them in — newest
   * first, so the newer neighbour is the one before it in the list.
   */
  const at = state.workouts.findIndex((candidate) => candidate.id === workoutId)
  const newer = at > 0 ? state.workouts[at - 1] : undefined
  const older = at >= 0 ? state.workouts[at + 1] : undefined

  const liveDetails = service.workoutWithSets(workoutId)
  // A local-first delete removes the workout from shared state before navigation finishes.
  // Hold the page that was confirmed for deletion so its missing state never flashes between.
  const [departingDetails, setDepartingDetails] = useState<{
    workout: Workout
    sets: LoggedSet[]
  } | null>(null)
  const details =
    liveDetails ?? (departingDetails?.workout.id === workoutId ? departingDetails : null)

  /*
   * What the server says about a workout the device is not holding. What the timeline has
   * pulled is a window, so a workout older than it — or reached from a link or an address —
   * is absent from the pile without being absent from the account. `null` while asking.
   */
  // Keyed by what was asked, so an answer about another id — or about the try before a
  // retry — is never read as the answer to this one.
  const [lookup, setLookup] = useState<{ about: string; ask: number; answer: Lookup } | null>(null)
  const [asked, setAsked] = useState(0)
  useEffect(() => {
    if (details !== null) {
      return
    }
    let cancelled = false
    void service.loadWorkout(workoutId).then((answer) => {
      if (!cancelled) {
        setLookup({ about: workoutId, ask: asked, answer })
      }
    })
    return () => {
      cancelled = true
    }
    // `asked` is what a retry turns to run this again.
  }, [service, workoutId, details, asked])

  const heard =
    lookup !== null && lookup.about === workoutId && lookup.ask === asked ? lookup.answer : null

  // A page about a workout that is not there is about nothing, so it leaves for the timeline
  // and says so over it rather than standing there saying the workout is missing.
  const gone = heard === 'gone' && details === null
  // Said once, however many times leaving makes this run again.
  const said = useRef<string | null>(null)
  useEffect(() => {
    if (!gone || said.current === workoutId) {
      return
    }
    said.current = workoutId
    void navigate({ to: '/' })
    toast('That workout is gone')
  }, [gone, workoutId, navigate, toast])

  if (!details) {
    if (heard === 'unreachable') {
      return (
        <section className="space-y-4 py-16">
          <h1 className="text-2xl font-medium">This workout could not be reached</h1>
          <p className="text-muted">
            The connection did not answer, so there is no saying whether this workout is still
            there.
          </p>
          <button
            type="button"
            className="text-accent underline"
            onClick={() => {
              setAsked((count) => count + 1)
            }}
          >
            Try again
          </button>
        </section>
      )
    }
    return (
      <SkeletonBlock label="Loading this workout" className="space-y-4 py-16">
        <Skeleton className="h-9 w-56" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-5 w-40" />
      </SkeletonBlock>
    )
  }

  const { workout, sets } = details

  const shownSets = sets.filter((set) => setIsShown(set))
  const stats = summaryStats(shownSets, sets, units)
  const notes = workoutNotes(workout)

  /*
   * What the body was around this workout, what the day walked and what it ate are none of
   * this page's business: they are the day's, and the day is behind the date this page already
   * carries — see `/specs/features/day-summary.spec.md`. The counts below are the workout's own.
   */
  const day = dayKey(workout.startedAt)

  const ascending = [...sets].sort((a, b) => a.performedAt - b.performedAt)
  /*
   * By exercise: a block for each exercise, holding every set of it wherever it was performed,
   * headed by what the whole of that exercise came to — warm-ups counted apart, which is why the
   * detail is read off every set rather than the ones the gallery keeps. An exercise left with
   * nothing but warm-ups has no tiles to head and is no block at all.
   */
  const byExercise = exerciseBlocksOf(ascending)
    .map((block) => ({
      exerciseName: block.exerciseName,
      detail: leadDetail(block.sets, units, distanceUnits),
      kept: block.sets
        .map((set, at) => ({ set, number: block.numbers[at] }))
        .filter(({ set }) => setIsShown(set)),
    }))
    .filter((block) => block.kept.length > 0)
  // In order: the one block the timeline draws, numbered over the whole workout first.
  const inOrderNumbers = numbersInRun(ascending)
  const inOrder = ascending
    .map((set, at) => ({ set, number: inOrderNumbers[at] }))
    .filter(({ set }) => setIsShown(set))

  // What the page is showing, in the order it shows it — the list a set opened from here is
  // leafed through.
  const leafed =
    layout === 'exercise'
      ? byExercise.flatMap((block) => block.kept.map(({ set }) => set))
      : inOrder.map(({ set }) => set)

  const chooseLayout = (next: WorkoutSetsLayout) => {
    if (next === layout) {
      return
    }
    beforeLayoutChange()
    setRelaidOut(true)
    setWorkoutSetsLayout(next)
  }

  return (
    <SetDeck sets={leafed}>
      <section className="space-y-10">
        <WorkoutNeighbours newer={newer} older={older} />

        {/*
        The workout's head, laid out the way an exercise's is — the same face, the same name and
        detail, the same counts as pills. It reads across rather than down its middle, because
        a workout has no curve to stand its face on and a face centred over nothing is a face
        floating in a gap.
      */}
        <div className={`flex items-center gap-5 sm:gap-6 ${HERO_FACE_SIZES}`}>
          {/* The poster's own colours held over it, coming off it as its light. */}
          <AmbientScope setIds={workoutTileIds(workout.id, state.sets)}>
            <div className="relative shrink-0">
              <AmbientGlow />
              {/* The workout wears on its page exactly the poster it wears in the timeline —
                the same card, grown to the head's size, so opening one lands on itself. */}
              <span
                role="img"
                aria-label={`${workout.name ?? 'Workout'} workout poster`}
                style={{ viewTransitionName: pageTransitionName('workout', workout.id) }}
                className="relative block h-[calc(var(--face)*1.5)] w-[var(--face)] overflow-hidden rounded-2xl bg-surface-raised [container-type:inline-size]"
              >
                <WorkoutPoster workout={workout} />
              </span>
            </div>
          </AmbientScope>

          <div className="page-enter min-w-0 flex-1" data-page-enter="1" data-testid="page-enter">
            <div className="flex items-start gap-3">
              <WorkoutName
                workout={workout}
                onRename={(named) => service.renameWorkout(workout.id, named)}
              />
              {/* Red, and a mark rather than a sentence: it sits beside the name the way the
                rename does, and the pair reads as two things you can do to the workout. */}
              <button
                type="button"
                aria-label="Delete workout"
                className="icon-btn shrink-0 text-danger hover:bg-danger/10 hover:text-danger"
                onClick={() => {
                  setDeleting(true)
                }}
              >
                <DeleteIcon className="h-7 w-7" />
              </button>
            </div>

            {/* How it felt is the poster's certificate and is not said again here — see
              `/specs/app.spec.md`, Workout viewer. The date is the way into the day: what the
              body was that morning and what the day ate are behind it. */}
            <p className="mt-1 text-sm text-ink-muted">
              <DaySummaryTrigger
                day={day}
                moment={{ time: timeOf(workout.startedAt), said: 'Before the workout' }}
                className="-mx-2 rounded-lg px-2 py-1 text-left transition-colors hover:bg-hover"
              >
                {workoutWhen(workout)}
              </DaySummaryTrigger>
            </p>

            {/*
            What was written either side of the workout, one line after the other with the
            opening first. Unheaded: two short lines under a workout's name read as what
            somebody wrote about it, and "Opening notes" over a sentence was a label taking
            more room than the thing it labelled.
          */}
            {notes.map(({ label, note }) => (
              <p key={label} className="mt-1 text-sm text-ink">
                {note}
              </p>
            ))}

            {/*
            What the workout came to. The counts are of the sets the filters leave, so a narrowed
            page never passes its own total off as the workout's. Each pill leads with its count
            and names it after; in the markup the pair is the other way round, because a term and
            its definition is what this list is.
          */}
            {sets.length > 0 && (
              <dl aria-label="What this workout came to" className="mt-4 flex flex-wrap gap-2">
                {stats.map((stat) => {
                  const Glyph = STAT_GLYPHS[stat.id]
                  return (
                    <div
                      key={stat.id}
                      className="flex items-center gap-2 rounded-full bg-surface-raised px-3.5 py-2.5 text-sm font-medium"
                    >
                      <Glyph className="h-5 w-5 shrink-0 text-ink-muted" />
                      <dt className="order-last">{stat.label}</dt>
                      <dd className={stat.narrowed ? 'text-accent' : undefined}>{stat.value}</dd>
                    </div>
                  )
                })}
              </dl>
            )}
          </div>
        </div>

        {/*
        The day's progress photos, where it has any. Its numbers are up in the chips with the
        workout's own; these are pictures, and pictures want their own room.
      */}
        <div className="page-enter" data-page-enter="3" data-testid="page-enter">
          <WeighInDayPhotos day={dayKey(workout.startedAt)} />
        </div>

        {sets.length > 0 && (
          <div className="page-enter" data-page-enter="3" data-testid="page-enter"></div>
        )}

        {sets.length === 0 ? (
          <div className="page-enter" data-page-enter="4" data-testid="page-enter">
            <EmptyState
              icon={<LayersIcon className="size-8" />}
              title="No logged sets"
              detail="This workout was saved without any completed sets."
            />
          </div>
        ) : inOrder.length === 0 ? (
          <div className="page-enter" data-page-enter="4" data-testid="page-enter">
            <EmptyState
              icon={<LayersIcon className="size-8" />}
              title="Nothing but warm-ups"
              detail="This workout holds no working sets."
            />
          </div>
        ) : (
          <div className="page-enter space-y-4" data-page-enter="4" data-testid="page-enter">
            {/* The choice stands over what it rearranges, at the end of the line. */}
            <div className="flex justify-end">
              <LayoutChoice layout={layout} onChoose={chooseLayout} />
            </div>
            <div ref={setsRoot}>
              {layout === 'exercise' ? (
                <div className="flex flex-col" style={{ gap: BLOCK_SPACING }}>
                  {byExercise.map((block) => (
                    <SetBlock
                      key={block.exerciseName}
                      exerciseName={block.exerciseName}
                      detail={block.detail}
                      sets={block.kept.map(({ set }) => set)}
                      numbers={block.kept.map(({ number }) => number)}
                      columns={6}
                      arriving={relaidOut}
                    />
                  ))}
                </div>
              ) : (
                // Headed by nothing, and dealt exactly as the timeline deals a workout's sets.
                <SetGrid
                  sets={inOrder.map(({ set }) => set)}
                  numbers={inOrder.map(({ number }) => number)}
                  columns={6}
                />
              )}
            </div>
          </div>
        )}

        {deleting && (
          <ConfirmDialog
            title={`Delete ${workout.name ?? 'this workout'}?`}
            body={`The workout goes, along with when it ran and what was written about it. Its ${sets.length} ${sets.length === 1 ? 'set goes' : 'sets go'} with it, and the videos of them.`}
            confirm="Delete"
            onConfirm={() => {
              setDeleting(false)
              setDepartingDetails(details)
              const named = workout.name ?? 'Workout'
              // The page is about a workout there no longer is, so it goes — and says so over
              // the timeline it lands on rather than standing there saying it is missing.
              void service.deleteWorkout(workout.id).then(() => {
                void navigate({ to: '/' })
                toast(`${named} deleted`)
              })
            }}
            onDismiss={() => {
              setDeleting(false)
            }}
          />
        )}
      </section>
    </SetDeck>
  )
}

/**
 * The two ways a workout's sets are laid out, as one control of two marks: stacked blocks for
 * by exercise, a clock for in order. Marks rather than words, because the choice is chrome over
 * the sets rather than something to read — and each still says what it is to anyone asking.
 */
function LayoutChoice({
  layout,
  onChoose,
}: {
  layout: WorkoutSetsLayout
  onChoose: (next: WorkoutSetsLayout) => void
}) {
  const options = [
    { value: 'exercise', label: 'By exercise', Glyph: ViewAgendaIcon },
    { value: 'order', label: 'In order', Glyph: ClockIcon },
  ] as const
  return (
    <div
      role="radiogroup"
      aria-label="How the sets are laid out"
      className="flex gap-1 rounded-full bg-surface p-1 ring-1 ring-white/10"
    >
      {options.map(({ value, label, Glyph }) => {
        const on = value === layout
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={label}
            title={label}
            className={`flex h-12 w-14 items-center justify-center rounded-full transition-colors ${
              on
                ? 'bg-accent/15 text-accent ring-1 ring-accent/40 ring-inset'
                : 'text-ink-muted hover:bg-hover hover:text-ink'
            }`}
            onClick={() => {
              onChoose(value)
            }}
          >
            <Glyph className="h-7 w-7" />
          </button>
        )
      })}
    </div>
  )
}

/**
 * The workout's name, and the way to change it. At rest it is the page's heading and nothing
 * else; under the pointer it takes the same wash every control in the app takes and shows the
 * mark of what pressing it does. Pressing turns it into the field it was already the size and
 * shape of, holding the name and ready to be typed in, with the way to keep the change beside
 * it and the way out beside that.
 *
 * Leaving with a change in it asks first. A half-typed name is work, and this app does not
 * throw work away without saying so.
 */
function WorkoutName({
  workout,
  onRename,
}: {
  workout: Workout
  onRename: (name: string) => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [abandoning, setAbandoning] = useState(false)
  const field = useRef<HTMLInputElement>(null)

  // The field is opened to be typed in, so it arrives with the cursor already in it.
  useEffect(() => {
    if (editing) {
      field.current?.focus()
    }
  }, [editing])

  const shown = workout.name ?? 'Workout'
  const trimmed = name.trim()
  const changed = name !== (workout.name ?? '')

  const stop = () => {
    setAbandoning(false)
    setEditing(false)
  }
  const save = () => {
    if (trimmed === '') {
      return
    }
    void onRename(trimmed).then(stop)
  }

  if (!editing) {
    return (
      <h1 className="min-w-0 flex-1 text-3xl font-medium tracking-tight">
        {/*
          The title on the page is a heading first and a control second, so the name is what
          the button is called and what it does is the description the pointer and the reader
          are both given.
        */}
        {/* The wash reaches just past the name rather than across the column: it is a target
            around the words, not a box drawn around the space beside them. */}
        <button
          type="button"
          title="Rename workout"
          className="group -mx-3 inline-flex max-w-full items-center gap-2 rounded-2xl px-3 py-1 text-left transition-colors hover:bg-hover"
          onClick={() => {
            setName(workout.name ?? '')
            setEditing(true)
          }}
        >
          <span className="min-w-0">{shown}</span>
          <EditIcon
            aria-hidden
            className="h-7 w-7 shrink-0 text-ink-muted opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          />
        </button>
      </h1>
    )
  }

  return (
    <>
      <form
        className="-mx-3 flex min-w-0 flex-1 items-center gap-1"
        onSubmit={(event) => {
          event.preventDefault()
          save()
        }}
      >
        {/* The field stands exactly where the heading did, at the size the heading was. */}
        <input
          ref={field}
          type="text"
          aria-label="Workout name"
          value={name}
          onChange={(event) => {
            setName(event.target.value)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault()
              if (changed) {
                setAbandoning(true)
              } else {
                stop()
              }
            }
          }}
          className="min-w-0 flex-1 rounded-2xl bg-surface-raised px-3 py-1 text-3xl font-medium tracking-tight text-ink outline-none focus:ring-2 focus:ring-accent"
        />
        <button
          type="submit"
          aria-label="Save name"
          disabled={trimmed === ''}
          className="icon-btn shrink-0 disabled:cursor-default disabled:opacity-40"
        >
          <CheckIcon className="h-7 w-7" />
        </button>
        <button
          type="button"
          aria-label="Stop renaming"
          className="icon-btn shrink-0"
          onClick={() => {
            if (changed) {
              setAbandoning(true)
            } else {
              stop()
            }
          }}
        >
          <CloseIcon className="h-7 w-7" />
        </button>
      </form>

      {abandoning && (
        <ConfirmDialog
          title="Throw the new name away?"
          body={`The workout goes back to being called ${shown}.`}
          confirm="Discard"
          onConfirm={stop}
          onDismiss={() => {
            setAbandoning(false)
          }}
          {...(trimmed === '' ? {} : { alt: 'Save', onAlt: save })}
        />
      )}
    </>
  )
}

/**
 * The workouts either side of this one, in the timeline's own order: the newer to the left,
 * the older to the right, the same sense the set viewer's arrows take. Each is a link to the
 * workout's own page, replacing rather than stacking: the page is one stop from the timeline
 * however many workouts have been walked through, so closing it goes back where it was opened
 * from rather than back through all of them.
 */
function WorkoutNeighbours({
  newer,
  older,
}: {
  newer: Workout | undefined
  older: Workout | undefined
}) {
  const navigate = useNavigate()
  const target = (label: string, workout: Workout | undefined) =>
    workout === undefined
      ? null
      : {
          label,
          title: workout.name ?? 'Workout',
          detail: formatDayLabel(workout.startedAt),
          link: { to: '/workouts/$workoutId' as const, params: { workoutId: workout.id } },
          go: () => {
            void navigate({
              to: '/workouts/$workoutId',
              params: { workoutId: workout.id },
              replace: true,
            })
          },
        }
  return <EdgeSteps left={target('Newer workout', newer)} right={target('Older workout', older)} />
}
